import { setAuthTokenGetter } from "./custom-fetch";

export interface TokenManagerOptions {
  accessTokenKey: string;
  /** @deprecated Refresh token is now stored in an HttpOnly cookie by the
   *  server and never written to localStorage.  This key is kept in the
   *  interface so existing call-sites don't break, but it is not used. */
  refreshTokenKey: string;
}

/**
 * Creates a token manager for a given storage key pair.
 *
 * Security model
 * ──────────────
 * • Access token  → localStorage (short-lived, 30 min — XSS window is small)
 * • Refresh token → HttpOnly cookie set by the server (XSS-invisible)
 *
 * On expiry the manager calls POST /api/auth/refresh with `credentials:'include'`
 * so the browser sends the HttpOnly cookie automatically.  The server rotates
 * both the cookie and returns a fresh access token in the JSON body.
 *
 * A lightweight `_session` flag in localStorage lets `hasSession()` return
 * true even after the access token has been evicted (e.g. on hard refresh),
 * so the app can attempt a silent refresh before redirecting to login.
 */
export function createTokenManager(opts: TokenManagerOptions) {
  const sessionFlagKey = `${opts.accessTokenKey}_session`;
  let _refreshPromise: Promise<string | null> | null = null;

  function isExpiredOrExpiringSoon(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]!)) as { exp?: number };
      const exp: number = payload.exp ?? 0;
      if (!exp) return false;
      return Date.now() / 1000 > exp - 60; // refresh 60 s before expiry
    } catch {
      return true;
    }
  }

  async function getValidToken(): Promise<string | null> {
    const token = localStorage.getItem(opts.accessTokenKey);
    if (token && !isExpiredOrExpiringSoon(token)) return token;

    // Access token absent or expiring — try a silent refresh via HttpOnly cookie.
    if (_refreshPromise) return _refreshPromise;

    _refreshPromise = (async () => {
      try {
        const response = await fetch("/api/auth/refresh", {
          method:      "POST",
          credentials: "include", // sends the HttpOnly refreshToken cookie
          headers:     { "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error("refresh-failed");
        const result = (await response.json()) as { accessToken: string };
        localStorage.setItem(opts.accessTokenKey, result.accessToken);
        localStorage.setItem(sessionFlagKey, "1");
        return result.accessToken;
      } catch {
        localStorage.removeItem(opts.accessTokenKey);
        localStorage.removeItem(sessionFlagKey);
        return null;
      } finally {
        _refreshPromise = null;
      }
    })();

    return _refreshPromise;
  }

  /**
   * Persist the access token and wire the auth getter.
   * The refresh token is intentionally ignored here — it lives in the
   * HttpOnly cookie set by the server's Set-Cookie header.
   */
  function saveTokens(accessToken: string, _refreshToken?: string): void {
    localStorage.setItem(opts.accessTokenKey, accessToken);
    localStorage.setItem(sessionFlagKey, "1");
    setAuthTokenGetter(getValidToken);
  }

  function clearTokens(...extraKeys: string[]): void {
    localStorage.removeItem(opts.accessTokenKey);
    localStorage.removeItem(sessionFlagKey);
    for (const key of extraKeys) localStorage.removeItem(key);
  }

  /**
   * Returns true if the user likely has a valid session.
   * Checks both the access token and the session flag so that a silent
   * refresh can be attempted even after the access token has expired.
   */
  function hasSession(): boolean {
    return !!(
      localStorage.getItem(opts.accessTokenKey) ||
      localStorage.getItem(sessionFlagKey)
    );
  }

  /** Call at app startup to rehydrate the auth getter from existing storage. */
  function init(): void {
    if (hasSession()) {
      setAuthTokenGetter(getValidToken);
    }
  }

  return { getValidToken, saveTokens, clearTokens, hasSession, init };
}
